import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';

export default function Personalizacion() {
  const [tipos, setTipos] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [requisitos, setRequisitos] = useState([]);
  const [tipoSel, setTipoSel] = useState(null);
  const [etapaSel, setEtapaSel] = useState(null);
  const [loading, setLoading] = useState(true);

  // inline edit states
  const [nuevoTipo, setNuevoTipo] = useState('');
  const [editTipoId, setEditTipoId] = useState(null);
  const [editTipoNombre, setEditTipoNombre] = useState('');
  const [nuevaEtapa, setNuevaEtapa] = useState('');
  const [editEtapaId, setEditEtapaId] = useState(null);
  const [editEtapaNombre, setEditEtapaNombre] = useState('');
  const [nuevoReq, setNuevoReq] = useState('');
  const [editReqId, setEditReqId] = useState(null);
  const [editReqTexto, setEditReqTexto] = useState('');
  const [toast, setToast] = useState(null);

  useEffect(() => { fetchTipos(); }, []);

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  async function fetchTipos() {
    setLoading(true);
    const { data } = await supabase.from('tipos_juicio').select('*').order('nombre');
    setTipos(data || []);
    setLoading(false);
  }

  async function fetchEtapas(tipoId) {
    const { data } = await supabase.from('etapas_plantilla').select('*').eq('tipo_juicio_id', tipoId).order('orden');
    setEtapas(data || []);
    setEtapaSel(null);
    setRequisitos([]);
  }

  async function fetchRequisitos(etapaId) {
    const { data } = await supabase.from('requisitos_plantilla').select('*').eq('etapa_id', etapaId).order('orden');
    setRequisitos(data || []);
  }

  async function selTipo(tipo) {
    setTipoSel(tipo);
    setEtapaSel(null);
    setRequisitos([]);
    setNuevaEtapa('');
    setNuevoReq('');
    await fetchEtapas(tipo.id);
  }

  async function selEtapa(etapa) {
    setEtapaSel(etapa);
    setNuevoReq('');
    await fetchRequisitos(etapa.id);
  }

  // --- TIPOS ---
  async function addTipo() {
    if (!nuevoTipo.trim()) return;
    await supabase.from('tipos_juicio').insert({ nombre: nuevoTipo.trim() });
    setNuevoTipo('');
    await fetchTipos();
    showToast('Tipo de acción creado');
  }

  async function saveTipo() {
    if (!editTipoNombre.trim()) return;
    await supabase.from('tipos_juicio').update({ nombre: editTipoNombre.trim() }).eq('id', editTipoId);
    setEditTipoId(null);
    await fetchTipos();
    if (tipoSel?.id === editTipoId) setTipoSel(t => ({ ...t, nombre: editTipoNombre.trim() }));
  }

  async function deleteTipo(id) {
    if (!window.confirm('¿Eliminar este tipo de juicio y todas sus etapas y requisitos?')) return;
    await supabase.from('tipos_juicio').delete().eq('id', id);
    if (tipoSel?.id === id) { setTipoSel(null); setEtapas([]); setEtapaSel(null); setRequisitos([]); }
    await fetchTipos();
    showToast('Tipo eliminado', 'error');
  }

  // --- ETAPAS ---
  async function addEtapa() {
    if (!nuevaEtapa.trim() || !tipoSel) return;
    const orden = etapas.length;
    await supabase.from('etapas_plantilla').insert({ tipo_juicio_id: tipoSel.id, nombre: nuevaEtapa.trim(), orden });
    setNuevaEtapa('');
    await fetchEtapas(tipoSel.id);
    showToast('Etapa creada');
  }

  async function saveEtapa() {
    if (!editEtapaNombre.trim()) return;
    await supabase.from('etapas_plantilla').update({ nombre: editEtapaNombre.trim() }).eq('id', editEtapaId);
    setEditEtapaId(null);
    await fetchEtapas(tipoSel.id);
    if (etapaSel?.id === editEtapaId) setEtapaSel(e => ({ ...e, nombre: editEtapaNombre.trim() }));
  }

  async function deleteEtapa(id) {
    if (!window.confirm('¿Eliminar esta etapa y todos sus requisitos?')) return;
    await supabase.from('etapas_plantilla').delete().eq('id', id);
    if (etapaSel?.id === id) { setEtapaSel(null); setRequisitos([]); }
    await fetchEtapas(tipoSel.id);
    showToast('Etapa eliminada', 'error');
  }

  async function moverEtapa(etapa, dir) {
    const idx = etapas.findIndex(e => e.id === etapa.id);
    const swap = etapas[idx + dir];
    if (!swap) return;
    await supabase.from('etapas_plantilla').update({ orden: swap.orden }).eq('id', etapa.id);
    await supabase.from('etapas_plantilla').update({ orden: etapa.orden }).eq('id', swap.id);
    await fetchEtapas(tipoSel.id);
  }

  // --- REQUISITOS ---
  async function addRequisito() {
    if (!nuevoReq.trim() || !etapaSel) return;
    const orden = requisitos.length;
    await supabase.from('requisitos_plantilla').insert({ etapa_id: etapaSel.id, texto: nuevoReq.trim(), orden });
    setNuevoReq('');
    await fetchRequisitos(etapaSel.id);
    showToast('Requisito creado');
  }

  async function saveRequisito() {
    if (!editReqTexto.trim()) return;
    await supabase.from('requisitos_plantilla').update({ texto: editReqTexto.trim() }).eq('id', editReqId);
    setEditReqId(null);
    await fetchRequisitos(etapaSel.id);
  }

  async function deleteRequisito(id) {
    await supabase.from('requisitos_plantilla').delete().eq('id', id);
    await fetchRequisitos(etapaSel.id);
    showToast('Requisito eliminado', 'error');
  }

  async function moverRequisito(req, dir) {
    const idx = requisitos.findIndex(r => r.id === req.id);
    const swap = requisitos[idx + dir];
    if (!swap) return;
    await supabase.from('requisitos_plantilla').update({ orden: swap.orden }).eq('id', req.id);
    await supabase.from('requisitos_plantilla').update({ orden: req.orden }).eq('id', swap.id);
    await fetchRequisitos(etapaSel.id);
  }

  if (loading) return <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ color: 'var(--text4)' }}>Cargando...</span></div>;

  const colStyle = { background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 12, display: 'flex', flexDirection: 'column', overflow: 'hidden' };
  const colHeaderStyle = { padding: '14px 16px', borderBottom: '0.5px solid var(--border)', fontWeight: 600, fontSize: 13, color: 'var(--text)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
  const itemStyle = (sel) => ({ padding: '10px 14px', fontSize: 13, cursor: 'pointer', borderBottom: '0.5px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, background: sel ? 'var(--blue-light)' : 'transparent', color: sel ? 'var(--blue)' : 'var(--text2)' });

  return (
    <div className="page">
      <div className="page-topbar">
        <div className="page-title">Personalización</div>
      </div>

      <div className="page-content">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, height: 'calc(100vh - 140px)' }}>

          {/* COLUMNA 1: TIPOS DE JUICIO */}
          <div style={colStyle}>
            <div style={colHeaderStyle}>
              <span>Tipos de acción</span>
              <span style={{ fontSize: 11, color: 'var(--text4)', fontWeight: 400 }}>{tipos.length} tipos</span>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {tipos.map(t => (
                <div key={t.id} style={itemStyle(tipoSel?.id === t.id)} onClick={() => selTipo(t)}>
                  {editTipoId === t.id ? (
                    <input
                      className="form-input"
                      style={{ flex: 1, fontSize: 13, padding: '4px 8px' }}
                      value={editTipoNombre}
                      onChange={e => setEditTipoNombre(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') saveTipo(); if (e.key === 'Escape') setEditTipoId(null); }}
                      onClick={e => e.stopPropagation()}
                      autoFocus
                    />
                  ) : (
                    <span style={{ flex: 1 }}>{t.nombre}</span>
                  )}
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    {editTipoId === t.id ? (
                      <>
                        <button className="btn btn-primary btn-xs" onClick={saveTipo}>✓</button>
                        <button className="btn btn-ghost btn-xs" onClick={() => setEditTipoId(null)}>✕</button>
                      </>
                    ) : (
                      <>
                        <button className="btn btn-ghost btn-xs" onClick={() => { setEditTipoId(t.id); setEditTipoNombre(t.nombre); }}>✏️</button>
                        <button className="btn btn-ghost btn-xs" style={{ color: 'var(--red)' }} onClick={() => deleteTipo(t.id)}>🗑</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {tipos.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text4)', fontSize: 13 }}>Sin tipos de juicio</div>}
            </div>
            <div style={{ padding: '10px 12px', borderTop: '0.5px solid var(--border)', display: 'flex', gap: 8 }}>
              <input
                className="form-input"
                style={{ flex: 1, fontSize: 13 }}
                placeholder="Nuevo tipo..."
                value={nuevoTipo}
                onChange={e => setNuevoTipo(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addTipo()}
              />
              <button className="btn btn-primary btn-sm" onClick={addTipo}>+</button>
            </div>
          </div>

          {/* COLUMNA 2: ETAPAS */}
          <div style={{ ...colStyle, opacity: tipoSel ? 1 : 0.5, pointerEvents: tipoSel ? 'all' : 'none' }}>
            <div style={colHeaderStyle}>
              <span>{tipoSel ? `Etapas — ${tipoSel.nombre}` : 'Etapas'}</span>
              <span style={{ fontSize: 11, color: 'var(--text4)', fontWeight: 400 }}>{etapas.length} etapas</span>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {etapas.map((e, i) => (
                <div key={e.id} style={itemStyle(etapaSel?.id === e.id)} onClick={() => selEtapa(e)}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
                    <button className="btn btn-ghost btn-xs" style={{ padding: '0 4px', lineHeight: 1 }} onClick={ev => { ev.stopPropagation(); moverEtapa(e, -1); }} disabled={i === 0}>▲</button>
                    <button className="btn btn-ghost btn-xs" style={{ padding: '0 4px', lineHeight: 1 }} onClick={ev => { ev.stopPropagation(); moverEtapa(e, 1); }} disabled={i === etapas.length - 1}>▼</button>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--text4)', minWidth: 18 }}>{i + 1}.</span>
                  {editEtapaId === e.id ? (
                    <input
                      className="form-input"
                      style={{ flex: 1, fontSize: 13, padding: '4px 8px' }}
                      value={editEtapaNombre}
                      onChange={ev => setEditEtapaNombre(ev.target.value)}
                      onKeyDown={ev => { if (ev.key === 'Enter') saveEtapa(); if (ev.key === 'Escape') setEditEtapaId(null); }}
                      onClick={ev => ev.stopPropagation()}
                      autoFocus
                    />
                  ) : (
                    <span style={{ flex: 1 }}>{e.nombre}</span>
                  )}
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={ev => ev.stopPropagation()}>
                    {editEtapaId === e.id ? (
                      <>
                        <button className="btn btn-primary btn-xs" onClick={saveEtapa}>✓</button>
                        <button className="btn btn-ghost btn-xs" onClick={() => setEditEtapaId(null)}>✕</button>
                      </>
                    ) : (
                      <>
                        <button className="btn btn-ghost btn-xs" onClick={() => { setEditEtapaId(e.id); setEditEtapaNombre(e.nombre); }}>✏️</button>
                        <button className="btn btn-ghost btn-xs" style={{ color: 'var(--red)' }} onClick={() => deleteEtapa(e.id)}>🗑</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {tipoSel && etapas.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text4)', fontSize: 13 }}>Sin etapas</div>}
            </div>
            <div style={{ padding: '10px 12px', borderTop: '0.5px solid var(--border)', display: 'flex', gap: 8 }}>
              <input
                className="form-input"
                style={{ flex: 1, fontSize: 13 }}
                placeholder="Nueva etapa..."
                value={nuevaEtapa}
                onChange={e => setNuevaEtapa(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addEtapa()}
              />
              <button className="btn btn-primary btn-sm" onClick={addEtapa}>+</button>
            </div>
          </div>

          {/* COLUMNA 3: REQUISITOS */}
          <div style={{ ...colStyle, opacity: etapaSel ? 1 : 0.5, pointerEvents: etapaSel ? 'all' : 'none' }}>
            <div style={colHeaderStyle}>
              <span>{etapaSel ? `Requisitos — ${etapaSel.nombre}` : 'Requisitos'}</span>
              <span style={{ fontSize: 11, color: 'var(--text4)', fontWeight: 400 }}>{requisitos.length} requisitos</span>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {requisitos.map((r, i) => (
                <div key={r.id} style={{ ...itemStyle(false), cursor: 'default', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0, marginTop: 2 }}>
                    <button className="btn btn-ghost btn-xs" style={{ padding: '0 4px', lineHeight: 1 }} onClick={() => moverRequisito(r, -1)} disabled={i === 0}>▲</button>
                    <button className="btn btn-ghost btn-xs" style={{ padding: '0 4px', lineHeight: 1 }} onClick={() => moverRequisito(r, 1)} disabled={i === requisitos.length - 1}>▼</button>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--text4)', minWidth: 18, marginTop: 4 }}>{i + 1}.</span>
                  {editReqId === r.id ? (
                    <input
                      className="form-input"
                      style={{ flex: 1, fontSize: 13, padding: '4px 8px' }}
                      value={editReqTexto}
                      onChange={e => setEditReqTexto(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') saveRequisito(); if (e.key === 'Escape') setEditReqId(null); }}
                      autoFocus
                    />
                  ) : (
                    <span style={{ flex: 1, lineHeight: 1.4 }}>{r.texto}</span>
                  )}
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0, marginTop: 2 }}>
                    {editReqId === r.id ? (
                      <>
                        <button className="btn btn-primary btn-xs" onClick={saveRequisito}>✓</button>
                        <button className="btn btn-ghost btn-xs" onClick={() => setEditReqId(null)}>✕</button>
                      </>
                    ) : (
                      <>
                        <button className="btn btn-ghost btn-xs" onClick={() => { setEditReqId(r.id); setEditReqTexto(r.texto); }}>✏️</button>
                        <button className="btn btn-ghost btn-xs" style={{ color: 'var(--red)' }} onClick={() => deleteRequisito(r.id)}>🗑</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
              {etapaSel && requisitos.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text4)', fontSize: 13 }}>Sin requisitos</div>}
            </div>
            <div style={{ padding: '10px 12px', borderTop: '0.5px solid var(--border)', display: 'flex', gap: 8 }}>
              <input
                className="form-input"
                style={{ flex: 1, fontSize: 13 }}
                placeholder="Nuevo requisito..."
                value={nuevoReq}
                onChange={e => setNuevoReq(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addRequisito()}
              />
              <button className="btn btn-primary btn-sm" onClick={addRequisito}>+</button>
            </div>
          </div>

        </div>
      </div>

      {toast && (
        <div className="toast" style={{ borderColor: toast.type === 'error' ? 'var(--red)' : 'var(--gold)' }}>
          {toast.type === 'error' ? '⚠️' : '✓'} {toast.msg}
        </div>
      )}
    </div>
  );
}
